import { formatDate } from '@angular/common';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';

export class StatementPdfGenerator {

  private static formatDateForDisplay(dateString: string): string {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }

  /**
   * Generate a PDF statement from account history data
   * @param accountHistory - Array of account history transactions
   * @param patientInfo - Patient information object
   * @param accountAgingBalance - Account balance information
   * @returns void - Triggers PDF download
   */
  static generateStatement(
    accountHistory: any[],
    patientInfo: any,
    accountAgingBalance: any,
    clinicInfo?: any
  ): void {
    try {
      
      // Create PDF document (A4 size)
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.width;
      
      
      // Group transactions by date
      const groupedTransactions = this.groupTransactionsByDate(accountHistory);
      
      // Add header
      this.addHeader(pdf, patientInfo, clinicInfo);
      
      // Add summary section
      this.addSummarySection(pdf, accountAgingBalance);
      
      // Start Y position after header and summary
      let yPos = 90;
      
      // Add each date group to the PDF
      Object.keys(groupedTransactions).forEach((dateKey, index) => {
        const group = groupedTransactions[dateKey];
        
        // Check if we need a new page
        if (yPos > 250) {
          pdf.addPage();
          yPos = 20;
        }
        
        // Add simplified date header with net impact
        const netImpact = group.dailyPaymentsTotal - group.dailyChargesTotal;
        const isNegative = netImpact < 0;
        
        // Date header with net impact
        pdf.setFillColor(240, 240, 240);
        pdf.rect(20, yPos - 5, pdf.internal.pageSize.width - 40, 10, 'F');
        
        // Date text
        pdf.setFontSize(11);
        pdf.setTextColor(60, 60, 60);
        pdf.text(this.formatDateForDisplay(dateKey), 25, yPos);
        
        // Net impact text
        pdf.setFontSize(11);
        pdf.setTextColor(isNegative ? 200 : 0, 0, isNegative ? 0 : 128, 0);
        const impactText = `Net: ${isNegative ? '-' : '+'}${Math.abs(netImpact).toFixed(2)}`;
        pdf.text(impactText, pdf.internal.pageSize.width - 60, yPos, { align: 'right' });
        
        yPos += 15;
        
        // Add transaction list
        const combinedTransactions = [...group.charges, ...group.payments].sort((a, b) => 
          a.code.localeCompare(b.code)
        );
        
        const tableColumn = ['Type', 'Service', 'Category', 'Amount'];
        const tableRows: any[] = [];
        
        combinedTransactions.forEach(transaction => {
          const serviceDesc = this.getServiceDescription(transaction.code);
          const sourceType = this.getSourceLabel(transaction.transactionSource || 'service');
          
          tableRows.push([
            transaction.transactionType,
            serviceDesc,
            sourceType,
            transaction.transactionType === 'Charge' 
              ? `${parseFloat(transaction.charge || 0).toFixed(2)}` 
              : `${parseFloat(transaction.credit || 0).toFixed(2)}`
          ]);
        });
        
        // Use autoTable function directly
        autoTable(pdf, {
          head: [tableColumn],
          body: tableRows,
          startY: yPos,
          margin: { left: 25, right: 25 },
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [240, 240, 240], textColor: [60, 60, 60] },
          columnStyles: {
            0: { cellWidth: 30 },
            1: { cellWidth: 70 },
            2: { cellWidth: 30 },
            3: { cellWidth: 'auto', halign: 'right' }
          },
          alternateRowStyles: { fillColor: [248, 248, 248] },
          didParseCell: function(data) {
            const rawData = data.row.raw as any[];
            
            // Set colors based on transaction type
            if (data.column.index === 0) {
              if (rawData && rawData[0] === 'Charge') {
                data.cell.styles.textColor = [200, 0, 0];
              } else if (rawData && rawData[0] === 'Credit') {
                data.cell.styles.textColor = [0, 128, 0];
              }
            }
            
            // Set colors based on source type
            if (data.column.index === 2) {
              if (rawData && rawData[2] === 'Adjustment') {
                data.cell.styles.textColor = [245, 124, 0]; // Orange
              } else if (rawData && rawData[2] === 'Insurance') {
                data.cell.styles.textColor = [25, 118, 210]; // Blue
              } else if (rawData && rawData[2] === 'Payment') {
                data.cell.styles.textColor = [123, 31, 162]; // Purple
              }
            }
            
            // Amount column color
            if (data.column.index === 3) {
              if (rawData && rawData[0] === 'Charge') {
                data.cell.styles.textColor = [200, 0, 0];
              } else if (rawData && rawData[0] === 'Credit') {
                data.cell.styles.textColor = [0, 128, 0];
              }
            }
          }
        });
        
        // Get the Y position after the table
        // @ts-ignore
        yPos = pdf.lastAutoTable.finalY + 10;
      });
      
      // Add footer
      this.addFooter(pdf, clinicInfo);
      
      // Save the PDF using Blob approach - more reliable in Angular
      try {
        const pdfBlob = pdf.output('blob');
        const url = window.URL.createObjectURL(pdfBlob);
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = 'patient-statement.pdf';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        setTimeout(() => {
          document.body.removeChild(downloadLink);
          window.URL.revokeObjectURL(url);
        }, 100);
      } catch (blobError) {
        // Fallback to direct save approach
        pdf.save('patient-statement.pdf');
      }
    } catch (error) {
      alert('There was an error generating your statement. Please try again later.');
    }
  }

  /**
   * Group transactions by date
   */
  private static groupTransactionsByDate(transactions: any[]): any {
    const grouped: any = {};
    
    // Filter out invalid dates
    const validTransactions = transactions.filter(t => 
      t.date && t.date !== "0001-01-01" && new Date(t.date).getFullYear() > 1900
    );
    
    // Sort by date (oldest first for running balance calculation)
    const sortedTransactions = [...validTransactions].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    
    // Group by date
    sortedTransactions.forEach(transaction => {
      const dateKey = transaction.date;
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          charges: [],
          payments: [],
          dailyChargesTotal: 0,
          dailyPaymentsTotal: 0,
          endingBalance: transaction.balance
        };
      }
      
      // Add to appropriate category
      if (transaction.transactionType === "Charge") {
        grouped[dateKey].charges.push(transaction);
        grouped[dateKey].dailyChargesTotal += parseFloat(transaction.charge || 0);
      } else {
        grouped[dateKey].payments.push(transaction);
        grouped[dateKey].dailyPaymentsTotal += parseFloat(transaction.credit || 0);
      }
      
      // Always use the latest balance as the ending balance
      grouped[dateKey].endingBalance = transaction.balance;
    });
    
    return grouped;
  }

  /**
   * Add document header with clinic and patient information
   */
  private static addHeader(pdf: jsPDF, patientInfo: any, clinicInfo?: any): void {
    // Use clinic info if available, otherwise use defaults
    const clinicName = clinicInfo?.displayName || 'HEALTH CLINIC';
    const clinicAddress1 = clinicInfo?.address || '123 Medical Center Drive';
    const clinicPhone = clinicInfo?.paymentPhoneNumber || clinicInfo?.phoneNumber || '';
    
    // Logo placeholder
    pdf.setFontSize(22);
    pdf.setTextColor(66, 0, 238); // Primary color
    pdf.text(clinicName, 20, 20);
    
    pdf.setFontSize(12);
    pdf.setTextColor(100, 100, 100);
    pdf.text(clinicAddress1, 20, 26);
    if (clinicPhone) {
      pdf.text(`Billing Inquiries: ${clinicPhone}`, 20, 32);
    }
    
    // Statement title
    pdf.setFontSize(18);
    pdf.setTextColor(66, 0, 238);
    pdf.text('ACCOUNT STATEMENT', 140, 20);
    
    // Statement date
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Statement Date: ${formatDate(new Date(), 'MMM dd, yyyy', 'en-US')}`, 140, 26);
    
    // Patient information
    pdf.setFontSize(11);
    pdf.setTextColor(60, 60, 60);
    pdf.text('Patient Information:', 140, 36);
    pdf.text(`Name: ${patientInfo?.name || 'Not available'}`, 140, 41);
    pdf.text(`Account #: ${patientInfo?.patientNumber || 'Not available'}`, 140, 46);
    
    // Divider line
    pdf.setDrawColor(200, 200, 200);
    pdf.line(20, 55, pdf.internal.pageSize.width - 20, 55);
  }

  /**
   * Add balance summary section
   */
  private static addSummarySection(pdf: jsPDF, accountAgingBalance: any): void {
    // Summary box
    pdf.setFillColor(245, 245, 255);
    pdf.setDrawColor(66, 0, 238);
    pdf.roundedRect(20, 60, pdf.internal.pageSize.width - 40, 25, 3, 3, 'FD');
    
    // Summary title
    pdf.setFontSize(14);
    pdf.setTextColor(66, 0, 238);
    pdf.text('Balance Summary', 25, 68);
    
    // Balance details
    pdf.setFontSize(10);
    pdf.setTextColor(60, 60, 60);
    
    // Left column
    pdf.text('Total Charges:', 25, 75);
    pdf.text('Insurance Coverage (Est.):', 25, 80);
    
    // Right column
    pdf.text('Your Responsibility:', 110, 75);
    pdf.text('Current Balance:', 110, 80);
    
    // Values - left column
    pdf.setTextColor(0, 0, 0);
    pdf.text(`$${accountAgingBalance?.Total?.toFixed(2) || '0.00'}`, 80, 75);
    pdf.text(`$${accountAgingBalance?.InsEst?.toFixed(2) || '0.00'}`, 80, 80);
    
    // Values - right column
    pdf.text(`$${accountAgingBalance?.PatEstBal?.toFixed(2) || '0.00'}`, 165, 75);
    pdf.setFontSize(11);
    pdf.setTextColor(66, 0, 238);
    pdf.text(`$${accountAgingBalance?.PatEstBal?.toFixed(2) || '0.00'}`, 165, 80);
  }

  /**
   * Add footer
   */
  private static addFooter(pdf: jsPDF, clinicInfo?: any): void {
    // Get page count - fix for TypeScript error
    const pageCount = pdf.internal.pages.length - 1; // Pages array is 1-indexed
    
    // Use clinic phone number if available, otherwise use a generic message
    const phoneNumber = clinicInfo?.paymentPhoneNumber || clinicInfo?.phoneNumber;
    const footerText = phoneNumber 
      ? `For questions about your bill, please contact our billing department at ${phoneNumber}.`
      : 'For questions about your bill, please contact our billing department.';
    
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      
      // Footer divider
      const yPos = pdf.internal.pageSize.height - 20;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(20, yPos, pdf.internal.pageSize.width - 20, yPos);
      
      // Footer text
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        footerText,
        pdf.internal.pageSize.width / 2,
        yPos + 5,
        { align: 'center' }
      );
      
      // Page number
      pdf.text(
        `Page ${i} of ${pageCount}`,
        pdf.internal.pageSize.width / 2,
        yPos + 10,
        { align: 'center' }
      );
    }
  }

  /**
   * Get service description from code
   */
  private static getServiceDescription(code: string): string {
    const serviceDescriptions: { [key: string]: string } = {
      // Standard services
      'OV1': 'Office Visit - New Patient',
      'OV2': 'Office Visit - Established Patient',
      'LAB': 'Laboratory Services',
      'RAD': 'Radiology',
      'PROC': 'Medical Procedure',
      'PAY': 'Payment Received',
      'INS': 'Insurance Payment',
      'T1356': 'Medical Imaging',
      'T1546': 'Lab Test - Standard',
      'T6357': 'Specialist Consultation',
      'Ins Pay Est': 'Estimated Insurance Payment',
      
      // Patient payments
      'PatPay': 'Payment Received',
      
      // Insurance payments
      'InsPay': 'Insurance Payment',
      'W/O': 'Insurance Write-Off',
      
      // Adjustments
      'Adj- Discount': 'Discount Applied',
      'Adj- Finance Charge': 'Finance Charge',
      'Adj- Insurance Adjustment': 'Insurance Adjustment',
      'Adj- Write Off': 'Write Off'
    };
    
    return serviceDescriptions[code] || `Service (${code})`;
  }

  /**
   * Get readable label for transaction source
   */
  private static getSourceLabel(source: string): string {
    const sourceMap: { [key: string]: string } = {
      'service': 'Service',
      'adjustment': 'Adjustment',
      'insurance': 'Insurance',
      'payment': 'Payment',
      'other': 'Other'
    };
    
    return sourceMap[source] || 'Service';
  }
}